import React, { useState, useEffect } from 'react';
import { getViewSystemData } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';

function ViewSystemModal({ isOpen, onClose, system }) {
  const [systemData, setSystemData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && system) {
      fetchSystemData();
    }
  }, [isOpen, system]);

  const fetchSystemData = async () => {
    try {
      setLoading(true);
      const data = await getViewSystemData(system.asset_id);
      setSystemData(data || system);
    } catch (error) {
      console.error('Error fetching system data:', error);
      // Fallback to the system data passed as prop
      setSystemData(system);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSystemData(null);
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="System Details">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  const data = systemData || system;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={data?.asset_name || 'System Details'}>
      <div className="space-y-4">
        {/* Compact System Header */}
        <div style={{ background: 'var(--gradient-primary)' }} className="rounded-md p-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-30 rounded">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div>
                <h2 className="font-semibold text-lg text-white">{data?.asset_name || 'System'}</h2>
                <p className="text-sm text-white opacity-90">Asset #{data?.asset_id}</p>
              </div>
            </div>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              data?.asset_status === 1 || data?.asset_status === 'active'
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              <div className="w-1.5 h-1.5 rounded-full bg-white mr-1.5"></div>
              {data?.asset_status === 1 || data?.asset_status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Compact Information Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* System Name */}
          <div className="bg-white border rounded-md p-3" style={{ borderColor: 'var(--border-light)' }}>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>System Name</label>
            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {data?.asset_name || 'N/A'}
            </div>
          </div>

          {/* Parent System */}
          <div className="bg-white border rounded-md p-3" style={{ borderColor: 'var(--border-light)' }}>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Parent System</label>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
              {data?.parent_name || (data?.asset_parent && data?.asset_parent !== '0' ? `System #${data.asset_parent}` : 'Root System')}
            </div>
          </div>

          {/* System Type and Description combined */}
          <div className="bg-white border rounded-md p-3 col-span-2" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-start justify-between">
              <div className="flex-1 mr-4">
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Type</label>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" 
                      style={{ 
                        backgroundColor: 'var(--antiflash-white)', 
                        color: 'var(--steel-blue)' 
                      }}>
                  {data?.asset_parent && data?.asset_parent !== '0' ? 'Sub-system' : 'Root System'}
                </span>
              </div>
              {data?.asset_description && (
                <div className="flex-2">
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Description</label>
                  <div className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                    {data.asset_description}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sub-systems Count */}
          {data?.children_count && parseInt(data.children_count) > 0 && (
            <div className="bg-white border rounded-md p-3" style={{ borderColor: 'var(--border-light)' }}>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sub-systems</label>
              <div className="text-sm font-semibold" style={{ color: 'var(--picton-blue)' }}>
                {data.children_count}
              </div>
            </div>
          )}

          {/* Cost if available */}
          {data?.asset_cost && parseFloat(data.asset_cost) > 0 && (
            <div className="bg-white border rounded-md p-3" style={{ borderColor: 'var(--border-light)' }}>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Cost</label>
              <div className="text-sm font-semibold" style={{ color: 'var(--color-success)' }}>
                R {parseFloat(data.asset_cost).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {/* Description if available - Remove this section since it's now inline */}

        {/* Support & SLA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border rounded-md p-3" style={{ borderColor: 'var(--border-light)' }}>
            <label className="block text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Support Contacts</label>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
              {data?.asset_support ? (
                <div className="whitespace-pre-line">{data.asset_support}</div>
              ) : (
                <span className="italic" style={{ color: 'var(--text-muted)' }}>No support contacts</span>
              )}
            </div>
          </div>

          <div className="bg-white border rounded-md p-3" style={{ borderColor: 'var(--border-light)' }}>
            <label className="block text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>SLA Response</label>
            <div className="text-sm">
              {data?.asset_sla ? (
                <span className="font-semibold" style={{ color: 'var(--color-success)' }}>{data.asset_sla} hours</span>
              ) : (
                <span className="italic" style={{ color: 'var(--text-muted)' }}>No SLA defined</span>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-3 border-t" style={{ borderColor: 'var(--border-light)' }}>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="px-4 py-2 text-sm"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ViewSystemModal;
